import { RootState } from "./store"
import { Message } from "./messagesSlice"
import { createSelector } from "@reduxjs/toolkit"
import ChatHelpers from "../lib/chatHelpers"
import { addSeconds, isAfter } from "date-fns"
import { selectChatMessages } from "./messagesSliceSelectors"
import { some } from "lodash"
import { selectIsRoomSyncFinished } from "./roomSyncStatusSelectors"
import { PinnedMessage } from "../api/xeps/PinnedMessages"

function isExpired(at: number, ttlInSeconds: number): boolean {
  // Convert timestamp to Date object
  const timestamp = new Date(at)

  // Add TTL to the timestamp
  const expirationTime = addSeconds(timestamp, ttlInSeconds)

  // Compare with current time
  const currentTime = new Date()

  // Return true if current time is after expiration time
  return isAfter(currentTime, expirationTime)
}

export const isMessagePinned = (message: Message) =>
  createSelector(
    (state: RootState) => state.pinned,
    (pinned) => {
      return Boolean(pinned[message.id])
    },
  )

export const allPinnedMessagesSelector = (state: RootState) => state.pinned

export const pinnedMessagesFromChat = (chatJid: string) =>
  createSelector(
    allPinnedMessagesSelector,
    (state: RootState) => state.chats,
    (pinned) => {
      return (
        Object.values(pinned).filter(
          ({ message, at, ttl }) =>
            ChatHelpers.isPinnedMessageFromChat(message, chatJid) &&
            !isExpired(at, ttl),
        ) ?? []
      )
    },
  )

export const countPinnedMessagesFromChatCreator = (chatJid: string) =>
  createSelector(pinnedMessagesFromChat(chatJid), (pinned) => pinned.length)

export const getPinnedMessageByPinnedId = (pinnedId: string) =>
  createSelector(allPinnedMessagesSelector, (pinned) => {
    return Object.entries(pinned).find(
      ([_, value]) => value.itemId === pinnedId,
    )?.[0]
  })

export const latestMissingPinnedMessage = (chatJid: string) =>
  createSelector(
    selectChatMessages(chatJid),
    pinnedMessagesFromChat(chatJid),
    selectIsRoomSyncFinished(chatJid),
    (chatMessages, pinned, syncFinished) => {
      if (!syncFinished) return undefined
      const missingIds = pinned
        .map(({ message }) => Number(message.id))
        .filter(
          (messageId) =>
            chatMessages?.messages.find(
              (m: Message) => m.id === `${messageId}`,
            ) === undefined,
        )
      if (missingIds.length === 0) return undefined
      return Math.min(...missingIds)
    },
  )

export const oldestPinnedMessageFromChatCreator = (chatJid: string) =>
  createSelector(pinnedMessagesFromChat(chatJid), (pinned) =>
    pinned.reduce(
      (oldest: PinnedMessage | undefined, current) =>
        !oldest || current.at < oldest.at ? current : oldest,
      undefined,
    ),
  )
